import { Button, TextField, Container, IconButton, Typography, Radio, RadioGroup, Box, Paper } from "@mui/material";
import DefaultUserIcon from "@mui/icons-material/Person";
import { alpha } from "@mui/material/styles";
import { styled as muiStyled } from "@mui/material/styles";
import styled, { keyframes } from 'styled-components';

// Dark theme color palette
export const PRIMARY = "#000000";
export const SECONDARY = "#1a1a1a";
export const ACCENT = "#2d2d2d";
export const SURFACE = "#121212";
export const ERROR = "#ff4444";
export const TEXT_PRIMARY = "#ffffff";
export const TEXT_SECONDARY = "#b0b0b0";
export const BORDER = "#333333";

export const NewChatButton = styled(Button)`
  background-color: ${PRIMARY} !important;
  color: ${TEXT_PRIMARY} !important;
`

export const ControlPanel = muiStyled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1),                   
  marginBottom: theme.spacing(2),
  backgroundColor: SURFACE,  
  borderRadius: theme.shape.borderRadius,       
  border: `1px solid ${BORDER}`,  
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',                           
}));

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const SidebarControls = muiStyled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  backgroundColor: SURFACE,
  borderRadius: 12,
}));

export const StyledSearchInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  height: 36,
  borderRadius: 20,
  backgroundColor: ACCENT,
  '& .MuiOutlinedInput-root': {
    borderRadius: 20,
    color: TEXT_PRIMARY,
    '& fieldset': {
      borderColor: BORDER,
      borderWidth: 1,
    },
    '&.Mui-focused fieldset': {
      borderColor: TEXT_PRIMARY,
      boxShadow: `0 0 0 2px ${alpha('#ffffff', 0.16)}`,
    },
  },
  '& .MuiInputBase-input': {
    color: TEXT_PRIMARY,
  },
  '& .MuiInputLabel-root': {
    color: TEXT_SECONDARY,
  },
}));

// Layout
export const LoginWrapper = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(135deg, ${PRIMARY} 0%, ${SECONDARY} 100%);
`;

export const LoginBox = styled('div')`
  background: ${SURFACE};
  padding: 40px 30px;
  border-radius: 25px;
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 360px;
  border: 1px solid ${BORDER};
`;

export const Logo = styled.img`
   margin-bottom: 15px; 
   align-self: center;
`;

export const FieldWrapper = styled.div`
  width: 100%;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const FieldLabel = styled.label`
  font-weight: 600;
  color: ${TEXT_PRIMARY};
  margin-bottom: 5px;
  font-size: 14px;
  letter-spacing: 0.5px;
`;

export const StyledInputWrapper = styled.div`
  width: 100%;
  padding: 16px 0;
  background-color: transparent; 
  position: sticky;
  bottom: 0;
  z-index: 100;
`;

export const StyledInput = styled.input`
  width: 100%;
  padding: 12px 18px;
  border-radius: 6px; 
  border: 1.5px solid ${BORDER};
  font-size: 15px;
  font-weight: 500;
  color: ${TEXT_PRIMARY};
  background-color: ${ACCENT};
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${TEXT_SECONDARY};
    font-weight: 400;
    opacity: 0.8;
  }

  &:hover {
    border-color: #555555;
  }

  &:focus {
    border-color: ${TEXT_PRIMARY};
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 600px) {
    font-size: 14px;
    padding: 10px 14px;
  }
`;

export const StyledButton = styled(Button)`
  width: 100%;
  margin-top: 10px !important;
  background-color: ${PRIMARY} !important;  
  color: ${TEXT_PRIMARY} !important;
  border-radius: 8px !important;
  font-weight: 600;
  text-transform: none;
  border: 1px solid ${BORDER};

  &:hover {
    background-color: ${SECONDARY} !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

export const ErrorText = styled(Typography)`
  color: ${ERROR};
  margin-bottom: 10px;
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1;
`;

export const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(135deg, ${PRIMARY} 0%, ${SECONDARY} 100%);
  color: ${TEXT_PRIMARY};
  padding: 0 20px;
`;

export const Heading = styled(Typography)`
  margin-bottom: 30px;
  font-weight: bold;
  font-size: 2rem;
  color: ${TEXT_PRIMARY};
`;

export const SettingsButton = styled(Button)`
  background-color: ${TEXT_PRIMARY};
  color: ${PRIMARY};
  border-radius: 20px;
  padding: 10px 20px;
  margin-top: 20px;
  font-weight: 600;
  text-transform: none;

  &:hover {
    background-color: ${TEXT_SECONDARY};
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
  }
`;

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  padding: 30px 20px;
  background: ${SURFACE};
  color: ${TEXT_PRIMARY};
  font-family: sans-serif;
  position: relative;
  
  @media (max-width: 600px) {
    padding: 20px 10px;
  }
`;

export const Title = styled.h2`
  color: ${TEXT_PRIMARY};
  margin-bottom: 8px;
`;

export const ConnectionForm = styled.div`
  margin-top: 20px;
  background: ${ACCENT};
  padding: 25px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  max-width: 600px;
  width: 100%;
  border: 1px solid ${BORDER};
  
  h3 {
    margin: 20px 0 10px;
    color: ${TEXT_PRIMARY};
  }
  
  @media (max-width: 600px) {
    padding: 20px 15px;
  }
`;

export const SaveButton = styled(Button)`
  margin-top: 25px !important;
  background-color: ${PRIMARY} !important;
  color: ${TEXT_PRIMARY} !important;
  font-weight: bold;
  text-transform: none !important;
  border-radius: 20px !important;
  border: 1px solid ${BORDER};

  &:hover {
    background-color: ${SECONDARY} !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
`;

export const CloseButton = styled(IconButton)`
  position: absolute;
  top: 15px;
  right: 15px;
  color: ${TEXT_PRIMARY} !important;

  &:hover {
    background-color: ${ACCENT};
  }
`;

export const FileInput = styled.input`
  margin: 15px 0;
  color: ${TEXT_PRIMARY};
  border: 1px dashed ${BORDER};
  padding: 12px;
  border-radius: 12px;
  width: 100%;
  background-color: ${ACCENT};
  cursor: pointer;
  font-family: inherit;
  
  &::file-selector-button {
    background: ${PRIMARY};
    border: none;
    color: ${TEXT_PRIMARY};
    padding: 8px 14px;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
  }
  
  &:hover {
    border-color: ${TEXT_PRIMARY};
  }
`;

export const StyledTextField = styled(TextField)`
  background-color: ${ACCENT} !important;
  border-radius: 20px !important;
  & label {
    color: ${TEXT_SECONDARY} !important;
  }
  & .MuiInputBase-input {
    color: ${TEXT_PRIMARY} !important;
  }
  & .MuiOutlinedInput-root {
    & fieldset {
      border-color: ${BORDER};
    }
    &:hover fieldset {
      border-color: ${TEXT_SECONDARY};
    }
    &.Mui-focused fieldset {
      border-color: ${TEXT_PRIMARY};
    }
  }
`;

export const SidebarWrapper = styled.div`
  width: 300px;
  background: linear-gradient(180deg, ${SURFACE} 0%, ${ACCENT} 100%);
  padding: 20px;
  overflow-y: auto;
  border-right: 1px solid ${BORDER};
`;

export const Header = styled.h2`
  color: ${TEXT_PRIMARY};
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: 700;
`;

export const NewChatButtonOld = styled.button`
  padding: 10px 18px;
  margin-bottom: 15px;
  border: 1px solid ${BORDER};
  border-radius: 30px;
  background-color: ${PRIMARY};
  color: ${TEXT_PRIMARY};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${SECONDARY};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
`;

export const SearchInput = styled.input`
  padding: 10px 14px;
  width: 100%;
  margin-bottom: 20px;
  border: 1px solid ${BORDER};
  border-radius: 25px;
  font-size: 14px;
  outline: none;
  transition: border 0.2s ease;
  background-color: ${ACCENT};
  color: ${TEXT_PRIMARY};

  &::placeholder {
    color: ${TEXT_SECONDARY};
  }

  &:focus {
    border-color: ${TEXT_PRIMARY};
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
  }
`;

export const ChatItemContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: ${ACCENT};
  border-radius: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  border: 1px solid ${BORDER};
`;

export const TitleText = styled.span`
  flex-grow: 1;
  margin-right: 8px;
  font-weight: 500;
  color: ${TEXT_PRIMARY};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChatItem = styled.div<{ selected: boolean }>`
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  background-color: ${({ selected }) => (selected ? SECONDARY : "transparent")};
  transition: background-color 0.2s ease;
  color: ${TEXT_PRIMARY};

  &:hover {
    background-color: ${ACCENT};
  }
`;

export const ChatContainer = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${SURFACE};
  width: 100%;
  color: ${TEXT_PRIMARY};
  overflow: hidden; 
  font-family: sans-serif;
  position: relative;
`;

export const Sidebar = styled.div<{ collapsed: boolean }>`
  width: 280px; 
  position: fixed;
  display: flex;     
  flex-direction: column; 
  top: 0;
  left: 0;
  height: 100vh;
  background: linear-gradient(180deg, ${SURFACE} 0%, ${ACCENT} 100%);
  /* reduce horizontal padding to avoid increasing effective width */
  padding: 20px;
  overflow-x: hidden;
  border-right: 1px solid ${BORDER};

  transform: ${({ collapsed }) => (collapsed ? "translateX(-280px)" : "translateX(0)")};
  transition: transform 0.3s ease-in-out;
  z-index: 1100;
`;

export const UserAvatarContainer = styled.div`
  display: flex;               
  align-items: center;       
  margin-top: auto;           
  padding: 15px;
  background-color: ${ACCENT};
  border-radius: 12px;
  border: 1px solid ${BORDER};
`;

export const SidebarHandle = styled('div')<{ collapsed: boolean }>`
  position: fixed;
  top: 16px;
  left: ${({ collapsed }) => (collapsed ? '8px' : 'calc(240px)')};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${PRIMARY};
  color: ${TEXT_PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  transition: all 0.3s ease;
  border: 1px solid ${BORDER};

  &:hover {
    background-color: ${SECONDARY};
    transform: scale(1.05);
  }

  z-index: 1100;
`;

export const ChatList = styled.div`
  margin-top: 16px;
  flex-grow: 1;
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 100px;
  max-height: calc(100vh - 250px);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${BORDER};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const ChatListItem = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 38px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 12px;

  font-weight: ${(props) => (props.active ? "600" : "500")};
  color: ${(props) => (props.active ? TEXT_PRIMARY : TEXT_SECONDARY)};
  background-color: ${(props) => (props.active ? SECONDARY : "transparent")};
  box-shadow: ${(props) =>
    props.active ? `inset 4px 0 0 ${TEXT_PRIMARY}` : "none"};

  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      props.active ? SECONDARY : ACCENT};
  }
`;

export const TitleText1 = styled.span`
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
  color: ${TEXT_PRIMARY};
`;

export const ChatTitle = styled.h2`
  color: ${TEXT_PRIMARY};
  margin-bottom: 15px;
  text-align: center;
  font-size: 1.1rem;
`;

export const AvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${TEXT_PRIMARY};
  cursor: pointer;
  border: 2px solid ${BORDER};
`;

export const DefaultAvatarIcon = styled(DefaultUserIcon)`
  font-size: 40px !important;
  color: ${PRIMARY};
  background: ${TEXT_PRIMARY};
  border-radius: 50%;
  cursor: pointer;
  padding: 4px;
  border: 2px solid ${BORDER};
`;

export const AvatarSettingsButton = styled(Button)`
  margin-top: 8px;
  font-size: 13px;
  padding: 4px 10px;
  background-color: ${ACCENT} !important;
  color: ${TEXT_PRIMARY} !important;
  text-transform: none;
  border-radius: 20px;
  border: 1px solid ${BORDER};

  &:hover {
    background-color: ${SECONDARY} !important;
  }
`;

export const ChatMain = styled.main<{ isSidebarCollapsed: boolean }>`
  flex: 1;
  margin-left: ${({ isSidebarCollapsed }) => (isSidebarCollapsed ? "0" : "280px")};
  transition: margin-left 0.3s ease;
  display: flex;
  flex-direction: column;
  background: ${SURFACE};
  overflow: hidden;
`;

export const MessagesWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
`;
export const InputWrapper = styled.div`
  display: flex;
  padding: 10px;
  background-color: ${ACCENT};
  flex-direction: row;
  align-items: center;
  border-top: 1px solid ${BORDER};
`;

export const SettingsPage = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  flex-direction: column;
  width: 50%;
  background-color: ${SURFACE};
  padding: 20px;
  color: ${TEXT_PRIMARY};
  overflow-y: auto;
  margin-left: 350px;
  border: 1px solid ${BORDER};
`;

export const SettingsInput = styled(TextField)`
  background-color: ${ACCENT} !important;
  border-radius: 25px !important;
  margin-bottom: 15px !important;
  & label {
    color: ${TEXT_SECONDARY} !important;
  }
  & .MuiInputBase-input {
    color: ${TEXT_PRIMARY} !important;
  }
  & .MuiOutlinedInput-root {
    & fieldset {
      border-color: ${BORDER};
    }
    &:hover fieldset {
      border-color: ${TEXT_SECONDARY};
    }
    &.Mui-focused fieldset {
      border-color: ${TEXT_PRIMARY};
    }
  }
`;

export const PreferencesSection = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid ${BORDER};
`;

export const ConnectionPopup = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  
  transform: translate(-50%, -50%);
  width: 400px;
  background: ${SURFACE};
  padding: 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.8);
  color: ${TEXT_PRIMARY};
  border: 1px solid ${BORDER};
`;

export const MessageContentWrapper = styled('div')<{ isUser: boolean }>`
  display: inline-block;
  background-color: ${({ isUser }) => (isUser ? SECONDARY : ACCENT)};
  color: ${TEXT_PRIMARY};
  padding: 12px 16px;
  border-radius: 20px;
  border-top-${({ isUser }) => (isUser ? 'right' : 'left')}-radius: 0;
  max-width: ${({ isUser }) => (isUser ? '65%' : '100%')};
  width: ${({ isUser }) => (isUser ? 'fit-content' : '100%')};
  white-space: pre-wrap;
  word-wrap: break-word;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid ${BORDER};
`;

export const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 100%;
  margin-bottom: 16px;
  display: flex;
  justify-content: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  animation: ${fadeInUp} 0.3s ease-out;
`;

export const SendButton = styled(Button)`
  border-radius: 50%;
  min-width: 48px;
  height: 48px;
  background-color: ${PRIMARY};
  color: ${TEXT_PRIMARY};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.6);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.3s ease;
  border: 1px solid ${BORDER};

  &:hover {
    background-color: ${SECONDARY};
    transform: scale(1.1);
    box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.8);
  }
  &:active {
    transform: scale(0.95);
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.4);
  }
`;

export const RadioGroupStyled = styled(RadioGroup)`
  margin-bottom: 20px;
  color: ${TEXT_PRIMARY};
`;

export const RadioStyled = styled(Radio)`
  color: ${TEXT_SECONDARY};
  &.Mui-checked {
    color: ${TEXT_PRIMARY};
  }
`;

export const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  text-align: center;
  background: ${SURFACE};
`;

export const EmptyTitle = styled(Typography)`
  && {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${TEXT_PRIMARY};
    margin-bottom: 8px;
  }
`;

export const EmptySubtitle = styled(Typography)`
  && {
    font-size: 1rem;
    color: ${TEXT_SECONDARY};
    margin-bottom: 24px;
  }
`;